import { ReactNode } from "react";

import { EXPORT_PAGES } from "./seo-pages-exports";
import { FORMAT_PAGES } from "./seo-pages-formats";
import { LANGUAGE_PAGES } from "./seo-pages-languages";
import { PLATFORM_PAGES } from "./seo-pages-platforms";
import { SCENARIO_PAGES } from "./seo-pages-scenarios";
import { SOURCE_PAGES } from "./seo-pages-sources";

/** Тематическая группа страницы — по ней строятся хабы и блок «Похожие страницы» */
export type SeoCluster = "format" | "language" | "usecase" | "platform" | "export";

export interface SeoPageConfig {
  path: string;
  metaTitle: string;
  metaDescription: string;
  customTitle: ReactNode; // H1
  customSubtitle: string;
  seoDescription?: ReactNode; // Новый блок текста для уникальности
  // seoText удален, всё перенесено в faq
  faq: {
    question: string;
    answer: string;
  }[];
  cluster?: SeoCluster;
  /** Короткое название для карточек в хабе и блоке похожих страниц */
  cardTitle?: string;
  /**
   * Slug'и статей блога, которые разворачивают тему страницы: рендерятся блоком
   * «Разбор в блоге» со ссылками на `/blog/<slug>`.
   *
   * Отдельным полем, а не `<Link>` внутри `seoDescription`, по технической
   * причине: этот файл попадает в граф `vite.config.ts` (его импортирует
   * генератор `llms.txt`), а конфиг сборки намеренно не тянет React.
   */
  relatedArticles?: string[];
  /**
   * Каноникал на другую страницу — для URL-дублей, которые нельзя убрать
   * без потери позиций. Такая страница остаётся доступной, но выпадает из sitemap.
   */
  canonicalPath?: string;
}

const BASE_PAGES: SeoPageConfig[] = [
  {
    path: "/mp3-to-text",
    relatedArticles: ["tochnost-rasshifrovki-99-procentov", "skolko-stoit-rasshifrovka-chasa-zapisi"],
    metaTitle: "MP3 в текст онлайн — спикеры и таймкоды | Полотно",
    metaDescription:
      "Загрузка MP3 до 2 ГБ, точность 95–99% на записях хорошего качества. Голосовые из Telegram в OGG и Opus — тоже без конвертации. Пробный период: 150 минут за 99 ₽.",
    customTitle: "Транскрибация MP3 в текст",
    customSubtitle:
      "Загрузите MP3 — получите текст с разделением по спикерам и таймкодами. Час записи обрабатывается за несколько минут.",
    seoDescription: (
      <>
        <p>
          Преобразование MP3 в текст — это эффективный способ сэкономить время при работе с аудиоматериалами. Наш сервис
          использует передовые алгоритмы распознавания речи, которые учитывают особенности произношения, терминологию и
          автоматически расставляют знаки препинания.
        </p>
        <p>
          Вы получаете не просто «полотно» текста, а структурированный документ с разделением на спикеров и таймкодами.
          Это идеальное решение для расшифровки записей совещаний, лекций или подкастов, где важно сохранить точность
          каждой реплики.
        </p>
        <p>
          MP3 остаётся самым универсальным форматом звука: его открывает любое устройство, и именно в нём чаще всего
          хранят подкасты, лекции и записи с диктофонов. Для расшифровки это удобно ещё и предсказуемостью размера —
          битрейт 128 кбит/с даёт примерно 1 МБ на минуту, то есть около 60 МБ за час записи.
        </p>
        <p>
          Даже четырёхчасовой максимум по длительности упирается всего в четверть гигабайта, так что до лимита в 2 ГБ
          отсюда очень далеко. С MP3 вопрос размера файла практически никогда не возникает — в отличие от несжатых
          форматов и видеоконтейнеров.
        </p>
        <p>
          Про битрейт стоит знать одно: для речи достаточно 128 кбит/с, а всё, что выше, на точность распознавания уже
          не влияет. А вот ниже — влияет заметно: на 64 кбит/с и меньше начинают теряться согласные и окончания слов, и
          в готовом тексте это видно сразу.
        </p>
        <p>
          Отсюда практическое правило: если запись изначально сделана в MP3, загружайте её как есть. Перекодировать
          сжатый звук в другой формат бессмысленно — качество от этого не вырастет, а каждая новая перекодировка
          добавляет потери поверх уже имеющихся.
        </p>
      </>
    ),
    faq: [
      {
        question: "Как перевести MP3 в текст?",
        answer:
          "Polotno использует передовые модели ИИ для автоматического распознавания. Просто перетащите MP3 файл в окно загрузки, выберите язык, и система сделает всё остальное."
      },
      {
        question: "В чем преимущества вашего конвертера?",
        answer:
          "Мы обеспечиваем высокую скорость (час аудио за 2-3 минуты), поддержку русского языка с пунктуацией, разделение на спикеров и встроенный редактор."
      },
      {
        question: "Как долго обрабатывается MP3 файл?",
        answer:
          "Обычно обработка занимает около 10-15% от длительности самого файла. Часовой файл будет готов через 5-7 минут."
      },
      {
        question: "Есть ли ограничения по размеру?",
        answer:
          "Мы поддерживаем файлы до 2 ГБ и длительностью до 4 часов. Для больших файлов рекомендуем использовать сжатие или загружать их по ссылке."
      },
      {
        question: "Насколько точна расшифровка?",
        answer:
          "Точность сильно зависит от записи. На чистом звуке — речь в микрофон, без наложений и сильного фона — 95–99%. На записи с одного микрофона в комнате, где несколько человек перебивают друг друга, результат заметно ниже: страдают в первую очередь имена, названия и числа."
      }
    ]
  },
  {
    path: "/voice-to-text",
    metaTitle: "Голос в текст онлайн | Запись речи в текст",
    metaDescription: "Преобразование голоса и диктофонных записей в текст. Расшифровка голосовых сообщений и интервью.",
    customTitle: "Преобразование голоса в текст",
    customSubtitle:
      "Идеально для журналистов, студентов и исследователей. Превратите ваши голосовые заметки в структурированный текст.",
    seoDescription: (
      <>
        <p>
          Перевод голоса в текст открывает новые возможности для обработки диктофонных записей и голосовых заметок.
          Вместо многочасового прослушивания вы получаете текст, по которому легко осуществлять поиск нужной информации.
        </p>
        <p>
          Инструмент оптимизирован для работы с записями разного качества: от профессиональных микрофонов до обычных
          смартфонов. Система автоматически фильтрует фоновые шумы и выделяет суть сказанного, помогая создавать
          конспекты и статьи в разы быстрее.
        </p>
        <p>
          Голосовые заметки копятся быстрее всего: надиктовать мысль на ходу занимает двадцать секунд, а вернуться к ней
          потом — сильно дольше, потому что послушать надо всё подряд. В текстовом виде десяток заметок просматривается
          за минуту, и по ним работает поиск.
        </p>
        <p>
          Диктовка на телефон даёт хорошее качество, когда говорят близко к микрофону. Портят результат обычно не
          устройство, а обстоятельства: ветер на улице, шум транспорта, разговор за рулём. Если заметка важная, лучше
          остановиться и надиктовать в тишине — точность вырастет заметно.
        </p>
        <p>
          Речь надиктованная и речь письменная устроены по-разному, и это стоит держать в голове. Расшифровка вернёт
          именно то, как вы говорили: с повторами, самоперебиваниями и «ну вот». Как черновик это отлично работает, как
          готовый текст — почти никогда.
        </p>
        <p>
          Отдельный удобный сценарий — расшифровка чужих голосовых из мессенджеров. Сохраните файл и загрузите как
          обычно: формат значения не имеет, а прочитать сообщение на пять минут гораздо быстрее, чем прослушать.
        </p>
      </>
    ),
    faq: [
      {
        question: "Как работает сервис перевода голоса?",
        answer:
          "Больше не нужно переслушивать часы записей. Polotno автоматизирует процесс расшифровки голосовых заметок, лекций и интервью, превращая звук в структурированный текст."
      },
      {
        question: "Могу ли я загрузить запись с диктофона?",
        answer: "Да, мы поддерживаем форматы m4a, wav, mp3, которые обычно используют диктофоны (включая iPhone)."
      }
    ]
  },
  {
    path: "/m4a-to-text",
    metaTitle: "Конвертер M4A в текст | Расшифровка аудио с iPhone",
    metaDescription:
      "Как перевести файл M4A в текст? Онлайн сервис транскрибации Polotno поддерживает формат Apple Voice Memos.",
    customTitle: "Конвертер M4A в текст",
    customSubtitle: "Специально для пользователей Apple: быстрая расшифровка записей диктофона и голосовых заметок.",
    seoDescription: (
      <>
        <p>
          Формат M4A часто используется в устройствах Apple, и наш сервис полностью оптимизирован для работы с ним. Не
          нужно искать сторонние конвертеры — просто загрузите файл из приложения «Диктофон» вашего iPhone прямо на
          сайт.
        </p>
        <p>
          Мы гарантируем сохранность качества при обработке сжатых форматов и обеспечиваем мгновенный экспорт результата
          в DOCX или PDF. Это самый быстрый путь превратить ваши мысли, записанные «на ходу», в готовый к редактированию
          текст.
        </p>
        <p>
          M4A — это контейнер, внутри которого почти всегда лежит кодек AAC. Именно в нём пишет стандартное приложение
          диктофона на iPhone и iPad, поэтому файл с таким расширением чаще всего означает голосовую заметку, интервью
          или запись встречи, сделанную на телефон.
        </p>
        <p>
          Записи с диктофона телефона удобны для распознавания тем, что устройство обычно лежит близко к говорящему. Это
          выгоднее, чем камера или ноутбук в углу комнаты: чем короче путь звука до микрофона, тем меньше эха и
          посторонних призвуков, и тем чище получается текст.
        </p>
        <p>
          По весу M4A компактен — час записи занимает порядка 30–60 МБ в зависимости от настроек качества. В лимит по
          размеру такие файлы не упираются никогда, а вот ограничение по длительности в четыре часа для длинных
          конференций учитывать стоит.
        </p>
        <p>
          Иногда M4A путают с M4B и M4R — это тот же контейнер, но с расширениями для аудиокниг и рингтонов. Если файл
          не принимается, достаточно переименовать расширение в .m4a: содержимое от этого не меняется, а формат
          определяется корректно.
        </p>
      </>
    ),
    faq: [
      {
        question: "Что такое формат M4A?",
        answer:
          "Это стандартный формат аудио для устройств Apple (iPhone, iPad, Mac). Polotno умеет работать с ним напрямую, конвертация не требуется."
      }
    ]
  },
  {
    path: "/meeting-minutes",
    metaTitle: "Протоколы совещаний и расшифровка Zoom/Meet",
    metaDescription:
      "Автоматическая расшифровка онлайн-встреч и создание протоколов (Meeting Minutes). AI-саммари и ключевые моменты.",
    customTitle: "Расшифровка совещаний и встреч",
    customSubtitle:
      "Загрузите запись Zoom, Google Meet или Teams. Получите полную стенограмму и краткое резюме встречи.",
    seoDescription: (
      <>
        <p>
          Ведение протоколов встреч (Meeting Minutes) вручную уходит в прошлое. Наш сервис автоматизирует этот процесс,
          позволяя участникам сосредоточиться на обсуждении, а не на конспектировании.
        </p>
        <p>
          Используя функции ИИ, мы не только расшифровываем диалоги, но и создаем краткое резюме встречи: принятые
          решения, назначенные задачи и ключевые сроки. Ваша команда всегда будет иметь четкую фиксацию результатов
          каждой встречи.
        </p>
        <p>
          Протокол, написанный по памяти через день после встречи, всегда отражает не то, что говорили, а то, что
          запомнил составитель. Расшифровка убирает эту прослойку: формулировки берутся из текста, а спорные места
          проверяются по таймкоду за секунды.
        </p>
        <p>
          Больше всего пользы это даёт в двух ситуациях. Первая — когда договорённости касаются денег и сроков и потом
          всплывают в переписке. Вторая — когда на встрече не было человека, которому по её итогам работать: ему нужен
          не пересказ, а то, что действительно прозвучало.
        </p>
        <p>
          Из полной расшифровки протокол собирается в два шага: сначала AI-сводка выделяет ключевые моменты, потом вы
          правите формулировки и добавляете ответственных. Второй шаг автоматизировать нельзя — кто за что взялся, часто
          остаётся невысказанным и додумывается участниками.
        </p>
        <p>
          Практический совет: рассылайте протокол в тот же день. Если у кого-то другое понимание договорённости,
          выяснится это сразу, пока запись под рукой, а не через месяц, когда её уже удалили по политике хранения.
        </p>
      </>
    ),
    faq: [
      {
        question: "Можно ли автоматизировать протоколы встреч?",
        answer:
          "Да. Забудьте о ручном ведении протоколов. Polotno не просто переводит речь в текст, но и (с помощью функции AI Summary) выделяет главные мысли, задачи и договоренности."
      }
    ]
  },
  {
    path: "/interview-transcription",
    metaTitle: "Транскрибация интервью для журналистов и исследователей",
    metaDescription: "Профессиональная расшифровка интервью. Точное разделение собеседников и таймкоды.",
    customTitle: "Транскрибация интервью",
    customSubtitle: "Фокусируйтесь на беседе, а не на конспектах. Быстрая расшифровка диалогов с разделением по ролям.",
    seoDescription: (
      <>
        <p>
          Для журналистов и социологов интервью — это основа работы. Мы понимаем, как важно сохранить каждую деталь и
          интонацию. Наш алгоритм распознавания речи специально обучен различать голоса нескольких собеседников, даже
          если они перебивают друг друга.
        </p>
        <p>
          Точное разделение на спикеров и удобная навигация по таймкодам позволяют быстро находить нужные цитаты. Не
          нужно тратить 4 часа на расшифровку часового разговора — получите готовый текст всего за несколько минут.
        </p>
        <p>
          Соотношение «час записи — четыре часа расшифровки» знает каждый, кто делал это руками: постоянные остановки,
          перемотки на пару секунд назад, попытки разобрать проглоченное окончание. Автоматическая расшифровка меняет не
          столько скорость, сколько характер работы — вместо перепечатки остаётся вычитка.
        </p>
        <p>
          Для журналиста ключевое — точность цитаты. Текст даёт формулировку, а таймкод рядом позволяет за секунду
          вернуться к записи и убедиться, что собеседник сказал именно это. При спорных материалах такая проверка
          защищает лучше любых заметок.
        </p>
        <p>
          Разделение по спикерам решает главную проблему интервью — где вопрос, а где ответ. Особенно это заметно в
          разговорах на троих и в тех местах, где собеседник перебивает или отвечает вопросом на вопрос.
        </p>
        <p>
          Условия записи стоит продумать заранее: кафе с музыкой и посудой — худший вариант из распространённых. Тихий
          кабинет или диктофон на столе между собеседниками дают заметно более чистый текст, и это экономит время именно
          на вычитке.
        </p>
      </>
    ),
    faq: [
      {
        question: "Как система различает собеседников?",
        answer:
          "Мы используем технологию диаризации (Speaker Diarization), которая автоматически определяет, кто говорит в данный момент, и помечает реплики как 'Спикер 1', 'Спикер 2' и т.д."
      }
    ]
  },
  {
    path: "/youtube-to-text-converter",
    metaTitle: "Текст из YouTube видео | Конвертер видео в текст онлайн",
    metaDescription:
      "Как быстро получить текст из видео YouTube? Вставьте ссылку и получите расшифровку за минуту. Пробный период — 150 минут за 99 ₽.",
    customTitle: "Текст из YouTube видео",
    customSubtitle:
      "Мгновенно превращайте любые видео с YouTube, VK или Rutube в текстовый формат. Просто вставьте ссылку.",
    seoDescription: (
      <>
        <p>
          Превращайте видеоконтент в текстовый формат для создания статей, постов для соцсетей или учебных конспектов.
          Вам не нужно скачивать видео — достаточно вставить ссылку на YouTube, VK Video или Rutube.
        </p>
        <p>
          Сервис мгновенно обрабатывает ссылку, извлекает аудиодорожку и формирует текст с учётом таймкодов. Это
          инструмент для контент-мейкеров и SEO-специалистов, позволяющий быстро перерабатывать видео в качественный
          текстовый контент.
        </p>
        <p>
          Автоматические субтитры YouTube и полноценная расшифровка — разные вещи. Первые идут без пунктуации, сплошным
          потоком, часто с ошибками на терминах, и выгрузить их в удобном виде получается не всегда. Отдельная
          расшифровка даёт оформленный текст с абзацами, спикерами и таймкодами.
        </p>
        <p>
          Работа по ссылке экономит основное время. Часовой ролик в хорошем качестве весит несколько гигабайт — их не
          нужно ни скачивать, ни загружать обратно: передаётся только адрес, а звук забирается на стороне сервиса.
        </p>
        <p>
          Ограничения касаются доступа и длительности. Приватные ролики и видео по ссылке с ограничениями сервису
          недоступны — их придётся скачать. Записи длиннее четырёх часов, например многочасовые стримы, нужно резать на
          части.
        </p>
        <p>
          Типичное применение — превратить свой же ролик в статью или расширенное описание. Поисковики видят текст, а не
          видео, поэтому текстовая версия на странице приводит людей по темам, о которых вы говорили в кадре.
        </p>
      </>
    ),
    faq: [
      {
        question: "Как скачать текст с YouTube видео?",
        answer:
          "1. Скопируйте ссылку на видео. 2. Вставьте её в поле ввода на этой странице. 3. Нажмите 'Начать' — система сама скачает аудиодорожку и преобразует её в текст."
      },
      {
        question: "Зачем нужна транскрибация видео?",
        answer:
          "Это полезно для учебы (конспекты лекций), SEO (создание статей из видео) и получения готовых субтитров (SRT)."
      },
      {
        question: "Поддерживаются ли длинные стримы?",
        answer: "Да, мы умеем обрабатывать видео длительностью до 4 часов. Это идеально подходит для стримов и лекций."
      },
      {
        question: "Можно ли скачать только субтитры?",
        answer: "Да, после обработки вы можете скачать результат в форматах TXT, DOCX, PDF или SRT (субтитры)."
      }
    ]
  },
  {
    path: "/transcription-online",
    relatedArticles: ["skolko-stoit-rasshifrovka-chasa-zapisi", "tochnost-rasshifrovki-99-procentov"],
    metaTitle: "Транскрибация онлайн — расшифровка аудио и видео в текст | Polotno",
    metaDescription:
      "Онлайн сервис транскрибации аудио и видео в текст. Точность 95–99% на записях хорошего качества, таймкоды и разделение по спикерам.",
    customTitle: "Транскрибация онлайн — расшифровка аудио и видео в текст",
    customSubtitle:
      "Загрузите файл или вставьте ссылку — получите точный текст за считанные минуты. Никакой ручной работы.",
    seoDescription: (
      <>
        <p>
          Транскрибация — это процесс преобразования устной речи в письменный текст. Раньше для этого требовались часы
          ручной работы, но современные технологии искусственного интеллекта позволяют автоматизировать этот процесс
          практически полностью.
        </p>
        <p>
          Polotno использует нейросетевые модели распознавания речи нового поколения: на записях хорошего качества
          точность составляет 95–99%. Сервис поддерживает десятки языков, автоматически расставляет знаки препинания,
          разделяет реплики по спикерам и добавляет таймкоды для удобной навигации по тексту.
        </p>
        <p>
          Онлайн-транскрибация подходит для самых разных задач: от расшифровки деловых встреч и лекций до создания
          субтитров для видео и подготовки текстового контента из аудиоматериалов.
        </p>
        <p>
          Онлайн-обработка означает, что ничего не нужно устанавливать и настраивать: браузер, файл, результат. Мощность
          компьютера при этом не имеет значения — распознавание идёт на сервере, и слабый ноутбук справится так же, как
          рабочая станция.
        </p>
        <p>
          Второе следствие — независимость от системы. Windows, macOS, Linux, планшет или телефон работают одинаково, а
          начать на одном устройстве и забрать результат на другом можно без всякой синхронизации: расшифровки лежат в
          истории аккаунта.
        </p>
        <p>
          Обработка занимает примерно десятую-седьмую часть от длительности записи, то есть час аудио готов минут за
          семь. Ждать в открытой вкладке не обязательно — процесс идёт на сервере, и результат дождётся в истории.
        </p>
        <p>
          Ограничения одинаковы для всех способов: до 2 ГБ и до четырёх часов на файл. Всё, что больше, нужно резать на
          части — либо загружать только аудиодорожку, если исходник был видео.
        </p>
      </>
    ),
    faq: [
      {
        question: "Что такое транскрибация?",
        answer:
          "Транскрибация — это перевод устной речи из аудио- или видеозаписи в текстовый формат. Polotno делает это автоматически с помощью ИИ, экономя часы ручной работы."
      },
      {
        question: "Какие форматы файлов поддерживаются?",
        answer:
          "MP3, M4A, WAV, OGG, FLAC, MP4, MOV, AVI, MKV и многие другие. Также можно вставить ссылку на YouTube, VK Video или Rutube."
      },
      {
        question: "Сколько стоит транскрибация?",
        answer:
          "Новым пользователям доступен пробный период — 150 минут за 99 ₽ на 7 дней. Далее можно приобрести пакет на 100, 500 или 1000 минут либо оформить месячную подписку Basic (450 минут за 598 ₽) или Pro (2000 минут за 1500 ₽) с более выгодной ценой за минуту."
      },
      {
        question: "Насколько точна автоматическая транскрибация?",
        answer:
          "Для записей хорошего качества точность достигает 95–99%. На результат влияет чёткость речи, отсутствие сильного фонового шума и качество микрофона."
      },
      {
        question: "Как быстро обрабатывается запись?",
        answer:
          "В среднем обработка занимает 10–15% от длительности записи. Часовой файл будет расшифрован примерно за 5–7 минут."
      },
      {
        question: "Можно ли редактировать результат?",
        answer:
          "Да, после расшифровки доступен встроенный редактор. Готовый текст можно скачать в форматах TXT, DOCX или PDF."
      }
    ]
  },
  {
    path: "/podcast-transcription",
    relatedArticles: ["tochnost-rasshifrovki-99-procentov"],
    metaTitle: "Расшифровка подкастов в текст онлайн | Polotno",
    metaDescription:
      "Автоматическая транскрибация подкастов в текст. Разделение по спикерам, таймкоды и AI-саммари. Превратите выпуск в статью за минуты.",
    customTitle: "Расшифровка подкастов в текст",
    customSubtitle:
      "Превратите каждый выпуск подкаста в готовую статью, пост или конспект. Автоматическое разделение по спикерам.",
    seoDescription: (
      <>
        <p>
          Подкастинг стремительно растёт, но аудиоформат имеет существенный недостаток — его сложно индексировать
          поисковыми системами. Транскрибация подкастов решает эту проблему, превращая каждый выпуск в текст, который
          можно публиковать на сайте, в блоге или социальных сетях.
        </p>
        <p>
          Polotno автоматически разделяет реплики ведущего и гостей, добавляет таймкоды и формирует структурированный
          документ. А функция AI Summary выделяет ключевые темы и тезисы выпуска, что идеально подходит для создания
          описаний эпизодов (show notes).
        </p>
        <p>
          Для подкастеров это не только SEO-инструмент, но и способ сделать контент доступным для людей с нарушениями
          слуха, а также удобный архив всех обсуждённых тем.
        </p>
        <p>
          Подкасты записываются лучше почти любого другого материала: студийные микрофоны, каждый участник на своей
          дорожке, минимум фонового шума. Точность на таких записях максимальная из возможного, и вычитки требуется
          заметно меньше, чем после совещаний или полевых интервью.
        </p>
        <p>
          Если дорожки участников сохранились по отдельности, их лучше обработать раздельно: разметка по спикерам
          получится идеальной, потому что голоса физически не накладываются. Для выпусков с гостями это особенно
          заметно.
        </p>
        <p>
          Из расшифровки собирается сразу несколько вещей: шоуноты с таймкодами по темам, цитаты для анонсов в соцсетях,
          полная текстовая версия выпуска на сайт. Последнее приводит слушателей из поиска — по подкастам люди ищут
          текстом, а не голосом.
        </p>
        <p>
          Имена гостей, названия компаний и упомянутые книги стоит проверять отдельно. Модель распознаёт их по звучанию,
          и незнакомая фамилия легко превращается в похожее слово — в шоунотах это выглядит небрежно.
        </p>
      </>
    ),
    faq: [
      {
        question: "Зачем расшифровывать подкаст?",
        answer:
          "Текстовая версия подкаста улучшает SEO сайта, позволяет публиковать статьи на основе выпусков и делает контент доступным для более широкой аудитории."
      },
      {
        question: "Как система различает ведущего и гостя?",
        answer:
          "Polotno использует технологию диаризации (Speaker Diarization), которая автоматически определяет смену спикеров и помечает каждую реплику."
      },
      {
        question: "Можно ли получить краткое содержание выпуска?",
        answer:
          "Да, функция AI Summary автоматически формирует краткое резюме с ключевыми темами и тезисами — готовые show notes для вашего подкаста."
      },
      {
        question: "Какой формат файла лучше загружать?",
        answer:
          "Подойдёт любой формат: MP3, M4A, WAV, OGG. Если подкаст опубликован на YouTube — просто вставьте ссылку."
      },
      {
        question: "Подходит ли сервис для длинных выпусков?",
        answer:
          "Да, мы поддерживаем записи длительностью до 4 часов. Двухчасовой выпуск будет обработан примерно за 15 минут."
      }
    ]
  },
  {
    path: "/lecture-transcription",
    metaTitle: "Расшифровка лекций в текст | Конспекты из аудио онлайн | Polotno",
    metaDescription:
      "Автоматическая расшифровка лекций и вебинаров в текст. Создавайте конспекты из записей занятий за минуты с помощью ИИ.",
    customTitle: "Расшифровка лекций и вебинаров в текст",
    customSubtitle:
      "Запишите лекцию и получите готовый конспект. Таймкоды, структурированный текст и краткое содержание от ИИ.",
    seoDescription: (
      <>
        <p>
          Студенты и преподаватели тратят десятки часов на конспектирование лекций. Автоматическая расшифровка позволяет
          сосредоточиться на понимании материала, а не на записывании — полный текст лекции будет готов через несколько
          минут после занятия.
        </p>
        <p>
          Polotno создаёт структурированные конспекты с таймкодами, что позволяет быстро находить нужные фрагменты при
          подготовке к экзаменам. Функция AI Summary выделяет ключевые тезисы и определения, формируя краткое содержание
          лекции — идеальную шпаргалку для повторения.
        </p>
        <p>
          Сервис работает с записями из Zoom, Google Meet, Microsoft Teams и любыми аудиофайлами с диктофона. Поддержка
          русского языка с автоматической пунктуацией обеспечивает качественный результат без дополнительной обработки.
        </p>
        <p>
          Лекция — это полтора часа связной подготовленной речи одного человека, то есть материал, на котором
          распознавание работает лучше всего. Проблемы начинаются только в двух местах: когда преподаватель отходит от
          микрофона к доске и когда из зала задают вопросы.
        </p>
        <p>
          Записывать лучше не с задней парты. Телефон на столе перед собой снимает голос лектора вместе с шумом
          аудитории, но чем ближе к источнику, тем чище результат. Разница между первым и последним рядом в тексте видна
          сразу.
        </p>
        <p>
          Главная выгода для студента не в самом тексте, а в том, что по нему работает поиск. Перед экзаменом вопрос
          «где он говорил про это» решается за секунды по расшифровкам всего семестра — переслушивать двадцать записей
          никто не станет.
        </p>
        <p>
          Формулы, схемы и всё, что показывалось на доске, в текст не попадут: сервис работает со звуком. Поэтому
          расшифровка дополняет конспект и слайды, а не заменяет их — особенно на технических предметах.
        </p>
      </>
    ),
    faq: [
      {
        question: "Как превратить запись лекции в конспект?",
        answer:
          "Загрузите аудио- или видеозапись лекции на Polotno. Система автоматически расшифрует речь, расставит знаки препинания и сформирует текст с таймкодами."
      },
      {
        question: "Можно ли загрузить запись из Zoom или Google Meet?",
        answer:
          "Да, скачайте запись встречи в любом формате (MP4, M4A, MP3) и загрузите на сайт. Также можно вставить ссылку, если запись опубликована на YouTube."
      },
      {
        question: "Подойдёт ли для лекций на русском языке?",
        answer:
          "Polotno оптимизирован для русского языка: автоматическая пунктуация, распознавание терминологии и высокая точность даже для сложных академических текстов."
      },
      {
        question: "Как использовать AI Summary для подготовки к экзаменам?",
        answer:
          "После расшифровки нажмите кнопку AI Summary — система выделит ключевые тезисы, определения и выводы лекции в виде краткого структурированного резюме."
      },
      {
        question: "Сколько стоит расшифровка лекции?",
        answer:
          "Новым пользователям доступен пробный период — 150 минут за 99 ₽ на 7 дней. Для регулярного использования выгоднее оформить подписку Basic (450 минут) или Pro (2000 минут) с увеличенным лимитом."
      }
    ]
  },
  {
    path: "/youtube-transcription",
    canonicalPath: "/youtube-to-text-converter",
    metaTitle: "Транскрибация YouTube видео в текст | Polotno",
    metaDescription:
      "Быстрая расшифровка видео с YouTube в текст. Вставьте ссылку и получите точный текст с таймкодами и разделением по спикерам.",
    customTitle: "Транскрибация YouTube видео в текст",
    customSubtitle:
      "Мгновенно превращайте видео с YouTube в точный текст. Вставьте ссылку и получите результат за минуты.",
    seoDescription: (
      <>
        <p>
          Скачивать ролик не нужно: достаточно скопировать ссылку из адресной строки. Сервис сам заберёт аудиодорожку,
          распознает речь и вернёт текст с таймкодами, привязанными к оригинальному видео.
        </p>
        <p>
          Чаще всего так поступают с лекциями и интервью, которые проще прочитать, чем досмотреть, и с длинными
          выступлениями, где нужен один конкретный фрагмент. Помимо YouTube поддерживаются ссылки на VK Video и Rutube.
        </p>
        <p>
          Поиск по видео — то, чего не умеет ни один плеер. В расшифровке нужное слово находится за секунду, а таймкод
          рядом показывает, куда перематывать. Для длинных интервью и разборов это меняет способ работы с материалом
          целиком.
        </p>
        <p>
          Списание идёт по полной длительности ролика, даже если вам нужен фрагмент из середины. Для двухчасовых записей
          это стоит прикинуть заранее — сервис показывает оценку до запуска, а вырезать нужный кусок и загрузить только
          его почти всегда дешевле.
        </p>
        <p>
          Языковых ограничений по ссылке нет: зарубежная лекция или интервью расшифруются на языке оригинала. Перевод
          при этом не выполняется, но перевести готовый текст любым переводчиком несравнимо проще, чем переводить со
          слуха.
        </p>
        <p>
          Не сработает ссылка в двух случаях: ролик закрыт настройками приватности или доступен только по прямой ссылке
          с ограничениями. Тогда остаётся обычный путь — скачать файл и загрузить его.
        </p>
      </>
    ),
    faq: [
      {
        question: "Нужно ли скачивать видео перед расшифровкой?",
        answer: "Нет, просто вставьте ссылку на ролик — всё остальное произойдёт автоматически."
      },
      {
        question: "Работают ли ссылки на VK Video и Rutube?",
        answer: "Да, помимо YouTube поддерживаются VK Video и Rutube."
      },
      {
        question: "Можно ли расшифровать видео с закрытым доступом?",
        answer:
          "Нет, ролик должен быть доступен по ссылке без авторизации. Приватное видео придётся скачать и загрузить файлом."
      },
      {
        question: "Какая максимальная длительность ролика?",
        answer: "До четырёх часов. Более длинные записи нужно разделить на части."
      }
    ]
  },
  {
    path: "/subtitle-generator",
    metaTitle: "Автоматические субтитры из видео (SRT/VTT) | Polotno",
    metaDescription:
      "Генерация субтитров из видео онлайн. Скачивайте готовый файл SRT с таймкодами для YouTube, VK Video и социальных сетей.",
    customTitle: "Автоматическая генерация субтитров (SRT)",
    customSubtitle: "Создавайте субтитры для ваших видео автоматически. Скачивайте SRT для YouTube и соцсетей.",
    seoDescription: (
      <>
        <p>
          Субтитры давно перестали быть опцией: большинство роликов в социальных сетях смотрят без звука, а без текста
          такое видео просто пролистывают. Ручная разметка часового ролика занимает несколько часов, автоматическая —
          несколько минут.
        </p>
        <p>
          Сервис распознаёт речь, разбивает её на реплики и проставляет тайминги, а результат выгружает в SRT. Этот файл
          загружается в YouTube, VK Video и Rutube как готовые субтитры или подставляется в видеоредактор для вшивания в
          картинку.
        </p>
        <p>
          SRT — самый универсальный формат субтитров: его понимают YouTube, VK Video, Rutube, плееры и монтажные
          программы. Внутри это простой текстовый файл с номерами реплик и таймингами, поэтому при необходимости его
          можно поправить в обычном блокноте.
        </p>
        <p>
          Тайминги проставляются автоматически по реплике, и в большинстве случаев их достаточно. Ручной правки обычно
          требуют только длинные фразы: субтитр из двадцати слов физически не читается за время, пока он на экране, и
          такие места лучше разбить надвое.
        </p>
        <p>
          Основной довод в пользу субтитров — просмотр без звука. Значительная часть аудитории в соцсетях смотрит ленту
          беззвучно, и ролик без текста для них просто пролистывается. Плюс субтитры делают видео доступным для людей с
          нарушениями слуха.
        </p>
        <p>
          Есть и польза для поиска: текст субтитров индексируется вместе со страницей, и видео начинает находиться по
          тому, что в нём говорят, а не только по названию и описанию.
        </p>
      </>
    ),
    faq: [
      {
        question: "В каком формате выгружаются субтитры?",
        answer: "В SRT с таймкодами — он понятен YouTube, VK Video, Rutube и всем распространённым видеоредакторам."
      },
      {
        question: "Можно ли поправить текст перед выгрузкой?",
        answer: "Да, после расшифровки доступен встроенный редактор: реплики можно исправить, объединить или разбить."
      },
      {
        question: "Разделяются ли субтитры по говорящим?",
        answer:
          "Да, диаризация определяет смену спикера. Это удобно для интервью и диалогов, где важно понимать, кому принадлежит реплика."
      }
    ]
  }
];

/** Страницы из базового набора, которые по смыслу относятся к форматам файлов */
const FORMAT_PATHS_IN_BASE = ["/mp3-to-text", "/m4a-to-text"];

const withCluster = (pages: SeoPageConfig[], cluster: SeoCluster): SeoPageConfig[] =>
  pages.map(page => ({ ...page, cluster }));

export const SEO_PAGES: SeoPageConfig[] = [
  ...BASE_PAGES.map(page => ({
    ...page,
    cluster: (FORMAT_PATHS_IN_BASE.includes(page.path) ? "format" : "usecase") as SeoCluster
  })),
  ...withCluster(FORMAT_PAGES, "format"),
  ...withCluster(LANGUAGE_PAGES, "language"),
  // Платформы созвонов и видеохостинги отвечают на один вопрос — откуда взялась запись
  ...withCluster([...PLATFORM_PAGES, ...SOURCE_PAGES], "platform"),
  ...withCluster(SCENARIO_PAGES, "usecase"),
  ...withCluster(EXPORT_PAGES, "export")
];

export const getPagesByCluster = (cluster: SeoCluster): SeoPageConfig[] =>
  SEO_PAGES.filter(page => page.cluster === cluster);

/** Заголовок страницы для карточек: cardTitle, иначе строковый H1, иначе часть metaTitle */
export const getCardTitle = (page: SeoPageConfig): string => {
  if (page.cardTitle) {
    return page.cardTitle;
  }
  if (typeof page.customTitle === "string") {
    return page.customTitle;
  }
  return page.metaTitle.split("|")[0].trim();
};
